import {
  CURRENT_CONTENT_SCHEMA_VERSION,
  EARLIEST_SUPPORTED_CONTENT_SCHEMA_VERSION,
  classifyContentSchemaVersion,
  migrateContentSchema,
  type ContentSchemaMigration,
  type ContentSchemaMigrationResult,
  type DomainCanvas,
  type DomainDocument,
  type JsonValue,
  type KnowledgeItem,
} from "@note-app/contracts";

import {
  assertFolder,
  assertKnowledgeItem,
  assertRevisionRecord,
  assertTag,
  type Folder,
  type RevisionRecord,
  type Tag,
} from "./entities.ts";
import { UnsupportedSchemaVersionError, ValidationError } from "./errors.ts";
import { assertCanvas, assertDocument } from "./validation.ts";

const documentMigrations: readonly ContentSchemaMigration<DomainDocument>[] = [
  {
    from: EARLIEST_SUPPORTED_CONTENT_SCHEMA_VERSION,
    to: CURRENT_CONTENT_SCHEMA_VERSION,
    migrate: (document) => ({
      ...document,
      schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
    }),
  },
];

const canvasMigrations: readonly ContentSchemaMigration<DomainCanvas>[] = [
  {
    from: EARLIEST_SUPPORTED_CONTENT_SCHEMA_VERSION,
    to: CURRENT_CONTENT_SCHEMA_VERSION,
    migrate: (canvas) => ({
      ...canvas,
      schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
    }),
  },
];

const knowledgeItemMigrations: readonly ContentSchemaMigration<KnowledgeItem>[] =
  [
    {
      from: EARLIEST_SUPPORTED_CONTENT_SCHEMA_VERSION,
      to: CURRENT_CONTENT_SCHEMA_VERSION,
      migrate: (item) => ({
        ...item,
        schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
      }),
    },
  ];

const folderMigrations: readonly ContentSchemaMigration<Folder>[] = [
  {
    from: EARLIEST_SUPPORTED_CONTENT_SCHEMA_VERSION,
    to: CURRENT_CONTENT_SCHEMA_VERSION,
    migrate: (folder) => ({
      ...folder,
      schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
    }),
  },
];

const tagMigrations: readonly ContentSchemaMigration<Tag>[] = [
  {
    from: EARLIEST_SUPPORTED_CONTENT_SCHEMA_VERSION,
    to: CURRENT_CONTENT_SCHEMA_VERSION,
    migrate: (tag) => ({
      ...tag,
      schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
    }),
  },
];

const revisionMigrations: readonly ContentSchemaMigration<RevisionRecord>[] = [
  {
    from: EARLIEST_SUPPORTED_CONTENT_SCHEMA_VERSION,
    to: CURRENT_CONTENT_SCHEMA_VERSION,
    migrate: (revision) => {
      const snapshot = revision.entityType === "document"
        ? migrateDocumentToCurrent(
            revision.snapshot as unknown as DomainDocument,
          ).value
        : migrateCanvasToCurrent(
            revision.snapshot as unknown as DomainCanvas,
          ).value;
      return {
        ...revision,
        operation: revision.operation ?? "save",
        schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
        snapshot: snapshot as unknown as JsonValue,
      };
    },
  },
];

export function migrateDocumentToCurrent(
  document: DomainDocument,
): ContentSchemaMigrationResult<DomainDocument> {
  const migrated = migrateKnownContent(document, documentMigrations);
  assertDocument(migrated.value);
  return migrated;
}

export function migrateCanvasToCurrent(
  canvas: DomainCanvas,
): ContentSchemaMigrationResult<DomainCanvas> {
  const migrated = migrateKnownContent(canvas, canvasMigrations);
  assertCanvas(migrated.value);
  return migrated;
}

export function migrateKnowledgeItemToCurrent(
  item: KnowledgeItem,
): ContentSchemaMigrationResult<KnowledgeItem> {
  const migrated = migrateKnownContent(item, knowledgeItemMigrations);
  assertKnowledgeItem(migrated.value);
  return migrated;
}

export function migrateFolderToCurrent(
  folder: Folder,
): ContentSchemaMigrationResult<Folder> {
  const migrated = migrateKnownContent(folder, folderMigrations);
  assertFolder(migrated.value);
  return migrated;
}

export function migrateTagToCurrent(
  tag: Tag,
): ContentSchemaMigrationResult<Tag> {
  const migrated = migrateKnownContent(tag, tagMigrations);
  assertTag(migrated.value);
  return migrated;
}

export function migrateRevisionRecordToCurrent(
  revision: RevisionRecord,
): ContentSchemaMigrationResult<RevisionRecord> {
  const migrated = migrateKnownContent(revision, revisionMigrations);
  assertRevisionRecord(migrated.value);
  return migrated;
}

function migrateKnownContent<T extends { schemaVersion: number }>(
  value: T,
  migrations: readonly ContentSchemaMigration<T>[],
): ContentSchemaMigrationResult<T> {
  if (!Number.isSafeInteger(value.schemaVersion)) {
    throw new ValidationError([
      {
        code: "invalid-schema-version",
        path: "schemaVersion",
        message: "must be a positive safe integer",
      },
    ]);
  }
  const compatibility = classifyContentSchemaVersion(value.schemaVersion);
  if (compatibility === "future" || compatibility === "unsupported-old") {
    throw new UnsupportedSchemaVersionError(
      value.schemaVersion,
      CURRENT_CONTENT_SCHEMA_VERSION,
      compatibility,
    );
  }
  try {
    return migrateContentSchema(value, migrations);
  } catch (error) {
    throw new UnsupportedSchemaVersionError(
      value.schemaVersion,
      CURRENT_CONTENT_SCHEMA_VERSION,
      value.schemaVersion > CURRENT_CONTENT_SCHEMA_VERSION
        ? "future"
        : "unsupported-old",
    );
  }
}
